import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {WeeklyGrowthReportService} from 'src/app/services/weeklyGrowthReport.service';
import {ExcelService} from 'src/app/services/excelService';
import * as _ from 'lodash';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component WeeklyGrowthReportComponent
 * @selector app-weekly-growth-report
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `WeeklyGrowthReportComponent` is responsible for generating and displaying the Weekly Growth Report.
 * This report provides insights into the growth metrics of aquaculture cultures across different farm sections over time.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches weekly growth data based on selected farm sites and sections.
 * 2. **Hierarchical Selection**: Manages the selection flow: Business -> Farm Site -> Section.
 * 3. **Filtering**: Provides client-side filtering capabilities:
 *    - **Search**: Text-based search (currently placeholder implementation).
 *    - **Section Filter**: Filters the displayed report data by specific sections.
 * 4. **Role-Based Access**: Adapts initialization and data availability based on the logged-in user's role.
 * 5. **Export**: Facilitates the export of the weekly growth report to an Excel file.
 * 6. **UI State Management**: Handles loading states, form validation, and fullscreen toggling.
 *
 * ## User Roles & Permissions:
 * - **EMPLOYEE**:
 *   - Must search for and select a business entity.
 *   - Restricted from seeing certain business types (IDs: '62b3fa8998be51001d17a310', '618a18ac0f339c001e991b08').
 * - **Farm Manager**:
 *   - Automatically linked to their business.
 *   - Can only view farm sites assigned to them (via Farm Spend context).
 * - **Business Admin / Farmer**:
 *   - Automatically linked to their business.
 *   - Can view all farm sites associated with the business.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role (e.g., 'EMPLOYEE', 'Farm Manager').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 * - `loginPersonMobileNumber`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` checks `loginPersonType`.
 *    - If 'EMPLOYEE', loads filtered list of businesses.
 *    - If others, auto-loads farm sites for the logged-in business.
 * 2. **Selection**: User selects Business (if Employee) -> Farm Site -> Section (Optional).
 * 3. **Data Fetching**: `getWeeklyGrowthSummary` calls `WeeklyGrowthReportService` with selected section IDs.
 * 4. **Display**: Data is stored in `weeklyGrowthReportsData` and displayed.
 * 5. **Filtering**: `onFiltersApplied` filters the local dataset `dupWeeklyGrowthReportsData` based on criteria.
 *
 * ## UI State Management:
 * - **Loading**: `enableDisableDataLoader` controls the loading spinner.
 * - **Form**: `registerForm` manages required fields (Business, Location).
 * - **Validation**: `formSubmitted` tracks submission attempts.
 */
@Component({
  selector: 'app-weekly-growth-report',
  templateUrl: './weekly-growth-report.component.html',
  styleUrls: ['./weekly-growth-report.component.scss']
})
export class WeeklyGrowthReportComponent implements OnInit, OnDestroy {

  // Models related Properties
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businesses: Array<BusinessEntity>;

  /** @type {BusinessEntity} The currently selected business entity model. */
  public businessModel: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmsites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} The currently selected farm site model. */
  public farmsiteModel: FarmSiteModel;

  /** @type {Array<PondSectionModel>} List of sections within the selected farm site. */
  public sections: Array<PondSectionModel>;

  /** @type {PondSectionModel} The currently selected section model. */
  public sectionModel: PondSectionModel;

  /** @type {Array<any>} The list of weekly growth report data currently displayed. */
  public weeklyGrowthReportsData: Array<any>;

  /** @type {Array<any>} Master copy of the report data used for client-side filtering. */
  public dupWeeklyGrowthReportsData: Array<any>

  // Reactive Form Validation Properties
  /** @type {FormGroup} The main reactive form for report criteria selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public formSubmitted: boolean = false;

  // Functionality variables
  /** @type {boolean} Flag controlling the data loader spinner. */
  public enableDisableDataLoader: boolean = false;

  /** @type {string} The current search string entered by the user. */
  public search: string = '';

  /** @type {boolean} Flag to display a "No Data" message. */
  public NoDataMessage: boolean = false;

  /** @type {number} The current active page number. */
  public activePageNo: number;

  // Login Credentials
  /** @type {string} The role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} The name of the logged-in user's business. */
  public loginBusinessName: string;

  /** @type {string} The name of the logged-in user. */
  public loginPersonName: string;

  /** @type {string} The mobile number of the logged-in user. */
  public loginPersonMobileNumber: string;

  // Customized Business dropdown Properties
  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {string} The type of filter currently active. */
  public filterType: string;

  /** @type {boolean} Flag indicating if filters have been applied. */
  public filtersApplied: boolean;

  /** @type {boolean} Flag indicating if the submit action has been triggered successfully. */
  public isSubmitClicked: boolean;

  /** @type {number} Duplicate tracker for current page number. Default is `1`. */
  public dupCurrentPageNo: number = 1;

  /** @type {boolean} Flag indicating if a section filter is currently applied. */
  public sectionFilterApplied: boolean;

  /**
   * Constructor for WeeklyGrowthReportComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PondSectionService} sectionService - Service for pond section operations.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {WeeklyGrowthReportService} weeklyGrowthReportService - Service for fetching weekly growth reports.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public sectionService: PondSectionService, public formBuilder: FormBuilder,
              public weeklyGrowthReportService: WeeklyGrowthReportService, public excelService: ExcelService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.businessModel = new BusinessEntity();
    this.farmsiteModel = new FarmSiteModel();
    this.sectionModel = new PondSectionModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
      sectionField: [null],
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginPersonType`:
   *   - **EMPLOYEE**: Fetches all businesses (excluding specific IDs) for selection.
   *   - **Others**: Auto-fetches farm sites for the logged-in business ID.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit(): Promise<void> {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.search = '';
      this.sectionFilterApplied = false;
      this.isSubmitClicked = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      if (this.loginPersonType == 'EMPLOYEE') {
        this.businessSearch = undefined;
        this.registerForm.get('businessfield').patchValue('');
        const businesses = await this.businessEntityService.getAllBusiness();
        this.businesses = businesses.filter((businessObject: BusinessEntity) => !['62b3fa8998be51001d17a310', '618a18ac0f339c001e991b08'].includes(businessObject.businessType && businessObject.businessType.length > 0 && businessObject.businessType[0].businessTypeID));
        this.filteredOptions = this.businesses;
      } else {
        this.registerForm.get('businessfield').setValidators(null);
        this.registerForm.get('businessfield').updateValueAndValidity();
        this.getFarmSitesByBusinessID(localStorage.businessID);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businesses` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businesses.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @description Handles the selection of a business entity from the dropdown.
   * - Updates the form value and model.
   * - Triggers fetching of farm sites for the selected business.
   * @param {BusinessEntity} business - The selected business entity.
   * @side-effect Updates `businessModel`, `loginBusinessName`, `isDropdownOpen`, calls `getFarmSitesByBusinessID`.
   */
  public selectOption(business: BusinessEntity): void {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.loginBusinessName = '';
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.loginBusinessName = business.businessName;
    this.businessModel.id = business.id;
    if (business) {
      this.formSubmitted = false;
      this.getFarmSitesByBusinessID(business.id);
    }
  }

  /**
   * @method getFarmSitesByBusinessID
   * @async
   * @description Fetches farm sites associated with a specific business ID.
   * Logic varies based on `loginPersonType` (Farm Manager vs others).
   * Auto-selects the farm site if only one exists.
   * @param {string} businessId - The ID of the business.
   * @side-effect Updates `farmsites`, `farmsiteModel`, calls `getSectionsByFarmSiteID`.
   */
  public async getFarmSitesByBusinessID(businessId: string): Promise<void> {
    try {
      this.farmsites = [];
      this.sections = [];
      this.farmsiteModel.id = undefined;
      this.sectionModel.id = undefined;
      if (this.loginPersonType == 'Farm Manager') {
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
      } else {
        this.farmsites = await this.farmSiteService.getFarmeSitesByFarmId(businessId);
      }
      if (this.farmsites.length == 1) {
        this.farmsiteModel.id = this.farmsites[0].id;
        this.getSectionsByFarmSiteID(this.farmsiteModel.id);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getSectionsByFarmSiteID
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * Auto-selects the section if only one exists.
   * @param {string} farmSiteID - The ID of the farm site.
   * @side-effect Updates `sections`, `sectionModel`.
   */
  public async getSectionsByFarmSiteID(farmSiteID: string): Promise<void> {
    try {
      this.sections = [];
      this.sectionModel.id = undefined;
      this.sections = await this.sectionService.getSectionsByFarmsiteId(farmSiteID);
      if (this.sections.length == 1) {
        this.sectionModel.id = this.sections[0].id;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onFilterChange
   * @description Resets section filter variables when a primary filter changes.
   * @side-effect Resets `sectionModel.id`, `sectionFilterApplied`.
   */
  public onFilterChange() {
    this.sectionModel.id = undefined;
    this.sectionFilterApplied = false;
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the weekly growth report list.
   * Orchestrates filtering based on:
   * - Text search (`search` property).
   * - Section filter (`sectionFilterApplied` property).
   * If no filters are active, it resets the list to the original dataset.
   * @side-effect Updates `weeklyGrowthReportsData`.
   */
  public onFiltersApplied() {
    this.weeklyGrowthReportsData = [];
    if (this.search != '') {
      if (this.sectionFilterApplied) {
        this.applySearchWithFilterType();
      } else {
        this.applySearchFilter();
      }
    } else {
      if (this.sectionFilterApplied) {
        this.applyFilterWithFilterType();
      } else {
        this.weeklyGrowthReportsData = this.dupWeeklyGrowthReportsData;
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Placeholder for text-based search filtering logic.
   * Currently empty implementation.
   */
  private applySearchFilter() {

  }

  /**
   * @method applyFilterWithFilterType
   * @private
   * @description Filters the report data based on the selected section ID.
   * @side-effect Updates `weeklyGrowthReportsData`.
   */
  private applyFilterWithFilterType() {
    this.weeklyGrowthReportsData = this.dupWeeklyGrowthReportsData.filter((weeklyGrowthDataObj: any) => weeklyGrowthDataObj.raw_data[0].section_id == this.sectionModel.id);
  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Placeholder for combined text search and section filtering logic.
   * Currently empty implementation.
   */
  private applySearchWithFilterType() {

  }

  /**
   * @method clearFilters
   * @description Clears specific filters based on the input type.
   * @param {string} filterFrom - The source of the clear action ('search' or default for section).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.sectionFilterApplied = false;
        this.sectionModel.id = undefined;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * @method getWeeklyGrowthSummary
   * @async
   * @description Fetches the weekly growth summary report from the server.
   * - Validates the form.
   * - Resets filters and search states.
   * - Calls the service to get data based on all available section IDs.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async getWeeklyGrowthSummary(): Promise<void> {
    this.formSubmitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.formSubmitted = false;
    this.dupWeeklyGrowthReportsData = [];
    this.isSubmitClicked = false;
    this.onFilterChange();
    this.search = '';
    this.filterType = '';
    try {
      this.enableDisableDataLoader = true;
      this.submitButtonDisabled = true;
      const requestBody = {
        'section_ids': _.map(this.sections, 'id')
      };
      this.weeklyGrowthReportsData = await this.weeklyGrowthReportService.getWeeklyGrowthReport(requestBody);
      this.dupWeeklyGrowthReportsData = this.weeklyGrowthReportsData;
      this.submitButtonDisabled = false;
      this.enableDisableDataLoader = false;
      this.isSubmitClicked = true;
      this.formSubmitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisableDataLoader = false;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method openFullScreen
   * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
   * @returns {void}
   */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method exportExcel
   * @description Generates and downloads an Excel report of the weekly growth summary.
   * Constructs the request body with current context and user details.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public exportExcel() {
    try {
      const requestBody = {
        'section_ids': _.map(this.sections, 'id'),
        'businessName': this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        'farmNameAtLocation': this.farmsites.find((farmSiteObject: FarmSiteModel) => farmSiteObject.id == this.farmsiteModel.id).farmNameAtLocation,
        'personName': localStorage.getItem('LoginUserName'),
        'mobileNumber': localStorage.getItem('loginPersonMobileNumber')
      };
      const fileObj = this.sectionService.excelReportsDownload('weeklyGrowth', requestBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Weekly Growth Summary Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.sectionService.cancelOnGoingHttpRequests();
    this.weeklyGrowthReportService.cancelOnGoingHttpRequests();
  }
}
